<template>
  <div v-if="showModuloForm === false">
    <MarketingModules></MarketingModules>
  </div>
  <div class="page" v-else-if="showModuloForm === true">
    <Panel lineColor="var(--primary-color)">
      <template v-slot:options>
        <FormButtonGroup align="start" spaced>
          <FormButton
            text="Voltar para listagem de cupons"
            type="auxiliar"
            :disabled="!getFornecedorId"
            beforeIcon="fas fa-arrow-left"
            @click="voltarListagemCupons"
          />
          <FormButton
            text="Exportar CSV"
            type="primario"
            :disabled="!getFornecedorId || exportando"
            :loading="exportando && formatoExportacao === 'csv'"
            @click="exportarSaldos('csv')"
          />
          <FormButton
            text="Exportar Excel"
            type="primario"
            :disabled="!getFornecedorId || exportando"
            :loading="exportando && formatoExportacao === 'xlsx'"
            @click="exportarSaldos('xlsx')"
          />
          <v-spacer></v-spacer>
          <FormButton
            text="Ajustar Saldo Cliente"
            color="primario"
            :disabled="!getFornecedorId"
            @click="abrirDialogSelecionarCliente"
          />
        </FormButtonGroup>
      </template>
      <DataTable
        :headers="headers"
        :endpoint="endpoint"
        :filters="tableFilters"
        :noHideFilters="true"
        :search="false"
        :autoSearch="false"
        sortBy="saldo_confirmado"
        :sortDesc="true"
        ref="datatable"
        :itemsPerPage="20"
      >
        <template v-slot:client_name="{ row }">
          <div class="saldo-pontos__cliente">
            <div>{{ row.client_name || "" }}</div>
            <div class="saldo-pontos__cliente-meta">
              {{ row.email || "" }}
            </div>
            <div class="saldo-pontos__cliente-meta">
              {{ formatarCpfCnpj(row.cpf) }}
            </div>
          </div>
        </template>
        <template v-slot:atendentes_padrao="{ col }">
          {{ col && String(col).trim() !== "" ? col : "" }}
        </template>
        <template v-slot:saldo_confirmado="{ col }">
          {{ formatarSaldo(col) }}
        </template>
        <template v-slot:saldo_pendente="{ col }">
          {{ formatarSaldo(col) }}
        </template>
        <template v-slot:saldo_expirado="{ col }">
          {{ formatarSaldo(col) }}
        </template>
        <template v-slot:data_atualizacao="{ col }">
          {{ formatDateTime(col) }}
        </template>
        <template v-slot:acao_ajustar="{ row }">
          <FormButton
            text="Ajustar"
            outlined
            color="primario"
            :disabled="!row.customer_id"
            @click.stop="abrirAjuste(row)"
          />
        </template>
      </DataTable>
    </Panel>
    <transition name="slide-popup" mode="out-in">
      <router-view class="view" @close="onCloseAjuste" />
    </transition>

    <v-dialog
      v-model="ajusteClienteDialogOpen"
      max-width="780"
      scrollable
      persistent
      :dark="darkEnabled"
    >
      <v-card :dark="darkEnabled" :class="ajusteClienteCardClass">
        <v-card-title class="pr-cfg-head pa-4">
          <v-btn
            icon
            small
            aria-label="Fechar"
            @click="fecharDialogSelecionarCliente"
          >
            <v-icon small>fas fa-times</v-icon>
          </v-btn>
          <span class="pr-cfg-title text-h6 flex-grow-1 text-center">
            Selecionar cliente para ajuste de saldo
          </span>
          <span class="pr-cfg-head-spacer" />
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-4 pt-6 pr-cfg-card-text">
          <div class="pr-cat-box pa-4">
            <div class="pr-section-label mb-2">
              <v-icon small :color="catBoxIconColor" class="mr-1"
                >fas fa-user</v-icon
              >
              <span>Cliente</span>
            </div>
            <p class="pr-hint mb-3">
              Pesquise por nome, e-mail ou CPF/CNPJ e selecione o cliente para
              abrir o painel de ajuste manual. Clientes que já aparecem na
              listagem também podem ser selecionados aqui.
            </p>
            <FormAutocomplete
              placeholder="Pesquisar cliente…"
              v-model="clienteAjusteSelecionado"
              :options="optionsClienteAjuste"
              :loading="buscandoClienteAjuste"
              clearable
              no-filter
              @search="onSearchClienteAjuste"
            />
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <FormButton
            text="Cancelar"
            type="error"
            @click="fecharDialogSelecionarCliente"
          />
          <FormButton
            text="Continuar"
            type="submissao"
            :disabled="!clienteAjusteSelecionado"
            @click="confirmarSelecaoClienteAjuste"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DataTable from "@/components/DataTable";
import Panel from "@/components/Panel";
import FormButton from "@/components/form/FormButton";
import FormButtonGroup from "@/components/form/FormButtonGroup";
import FormAutocomplete from "@/components/form/FormAutocomplete";
import { mapGetters } from "vuex";
import * as moment from "moment";
import MarketingModules from "@/components/MarketingModules";
import { ModulesEnum } from "@/store/actions/modulesEnum";
import { sanitizeDocument } from "@/utils/documentUtils";
import {
  PROGRAMA_RECOMPENSA_EXPORTAR_SALDOS,
  PROGRAMA_RECOMPENSA_BUSCAR_CLIENTES_CRITERIO
} from "@/store/actions/programaRecompensa";

function formatarDocumentoCnpjEstiloClienteForm(sanitized) {
  let i = 0;
  const take = maxLen => {
    const chunk = sanitized.slice(i, i + maxLen);
    i += chunk.length;
    return chunk;
  };
  const a = take(2);
  if (!a) {
    return sanitized;
  }
  let out = a;
  const b = take(3);
  if (b) {
    out += `.${b}`;
  }
  const c = take(3);
  if (c) {
    out += `.${c}`;
  }
  const d = take(4);
  if (d) {
    out += `/${d}`;
  }
  const e = take(2);
  if (e) {
    out += `-${e}`;
  }
  if (i < sanitized.length) {
    out += sanitized.slice(i);
  }
  return out;
}

export default {
  name: "ProgramaRecompensaSaldoPontos",
  components: {
    DataTable,
    Panel,
    FormButton,
    FormButtonGroup,
    FormAutocomplete,
    MarketingModules
  },
  data: () => ({
    ajusteClienteDialogOpen: false,
    clienteAjusteSelecionado: "",
    optionsClienteAjuste: [],
    buscandoClienteAjuste: false,
    exportando: false,
    formatoExportacao: "",
    modulosForm: [
      ModulesEnum.WEB_COMMERCE,
      ModulesEnum.COMMERCE_APP,
      ModulesEnum.VENDAMAIS_WEB,
      ModulesEnum.VENDAMAIS_APP
    ],
    headers: [
      {
        text: "Cliente",
        value: "client_name",
        align: "left",
        sortable: false,
        clickable: false
      },
      {
        text: "Atendente padrão",
        value: "atendentes_padrao",
        align: "left",
        sortable: false,
        clickable: false
      },
      {
        text: "Saldo confirmado",
        value: "saldo_confirmado",
        align: "right",
        sortable: true,
        clickable: false
      },
      {
        text: "Saldo pendente",
        value: "saldo_pendente",
        align: "right",
        sortable: true,
        clickable: false
      },
      {
        text: "Saldo expirado",
        value: "saldo_expirado",
        align: "right",
        sortable: true,
        clickable: false
      },
      {
        text: "Última atualização",
        value: "data_atualizacao",
        align: "center",
        sortable: true,
        clickable: false
      },
      {
        text: "Ações",
        value: "acao_ajustar",
        align: "center",
        sortable: false,
        clickable: false
      }
    ]
  }),
  computed: {
    ...mapGetters(["getFornecedorId", "moduloFornecedorAtivo", "darkEnabled"]),
    ajusteClienteCardClass() {
      return {
        "pr-cfg-card": true,
        "pr-cfg-card--dark": this.darkEnabled
      };
    },
    catBoxIconColor() {
      return this.darkEnabled ? "blue lighten-2" : "primary";
    },
    showModuloForm() {
      return this.moduloFornecedorAtivo(this.modulosForm);
    },
    endpoint() {
      return `/api/v4/programa-recompensa/saldo-pontos/listar/${this.getFornecedorId}`;
    },
    tableFilters() {
      const fid = this.getFornecedorId;
      return [
        {
          label: "Cliente (nome, e-mail, CPF/CNPJ, documento adicional)",
          query: "cliente",
          type: "text",
          default: "",
          cols: "4"
        },
        {
          label: "Vendedor / atendente",
          query: "vendedor",
          type: "autocomplete",
          clearable: true,
          default: "",
          request: {
            endpoint: `/api/v4/usuario/${fid}/pesquisa`,
            query: "q",
            value: "idusuario",
            text: "nome"
          }
        },
        {
          label: "Saldo confirmado mínimo",
          query: "saldo_min",
          type: "text",
          default: ""
        },
        {
          label: "Saldo confirmado máximo",
          query: "saldo_max",
          type: "text",
          default: ""
        }
      ];
    }
  },
  methods: {
    voltarListagemCupons() {
      this.$router.push({
        name: "ProgramaRecompensa"
      });
    },
    onCloseAjuste() {
      if (this.$refs.datatable) {
        this.$refs.datatable.reload();
      }
    },
    abrirDialogSelecionarCliente() {
      this.clienteAjusteSelecionado = "";
      this.optionsClienteAjuste = [];
      this.ajusteClienteDialogOpen = true;
    },
    fecharDialogSelecionarCliente() {
      this.ajusteClienteDialogOpen = false;
      this.clienteAjusteSelecionado = "";
      this.optionsClienteAjuste = [];
    },
    onSearchClienteAjuste(termo) {
      if (!this.getFornecedorId) {
        return;
      }
      this.buscandoClienteAjuste = true;
      this.$store
        .dispatch(PROGRAMA_RECOMPENSA_BUSCAR_CLIENTES_CRITERIO, {
          search: termo || ""
        })
        .then(options => {
          this.optionsClienteAjuste = options || [];
        })
        .catch(() => {
          this.optionsClienteAjuste = [];
        })
        .finally(() => {
          this.buscandoClienteAjuste = false;
        });
    },
    confirmarSelecaoClienteAjuste() {
      if (!this.clienteAjusteSelecionado) {
        return;
      }
      const opcao = this.optionsClienteAjuste.find(
        o => o.value === this.clienteAjusteSelecionado
      );
      const customerId = this.clienteAjusteSelecionado;
      const clientName = (opcao && opcao.text) || "";
      this.fecharDialogSelecionarCliente();
      this.abrirAjuste({
        customer_id: customerId,
        client_name: clientName
      });
    },
    /**
     * Navega para o painel lateral de ajuste (rota ProgramaRecompensaAjusteSaldo — BACK-604).
     */
    abrirAjuste(row) {
      if (!row || !row.customer_id) {
        return;
      }
      this.$router.push({
        name: "ProgramaRecompensaAjusteSaldo",
        params: { idcliente: row.customer_id },
        query: { clientName: row.client_name || "" }
      });
    },
    montarQueryExportacao(formato) {
      const params = new URLSearchParams();
      params.set("formato", formato);
      const filtros =
        (this.$refs.datatable && this.$refs.datatable.filtrosAvancados) || [];
      filtros.forEach(f => {
        const val = f.value;
        if (val == null || val === "") {
          return;
        }
        if (Array.isArray(val) && val.length === 0) {
          return;
        }
        params.set(f.query, String(val));
      });
      return params.toString();
    },
    extrairNomeArquivo(contentDisposition, fallback) {
      if (!contentDisposition) {
        return fallback;
      }
      const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i.exec(
        contentDisposition
      );
      if (!match || !match[1]) {
        return fallback;
      }
      return match[1].replace(/['"]/g, "").trim() || fallback;
    },
    async exportarSaldos(formato) {
      if (!this.getFornecedorId || this.exportando) {
        return;
      }
      this.exportando = true;
      this.formatoExportacao = formato;
      const fallbackName = `saldo_pontos.${
        formato === "xlsx" ? "xlsx" : "csv"
      }`;
      const query = this.montarQueryExportacao(formato);
      try {
        const res = await this.$store.dispatch(
          PROGRAMA_RECOMPENSA_EXPORTAR_SALDOS,
          { queryString: query }
        );
        const contentType = (res.headers && res.headers["content-type"]) || "";
        if (contentType.includes("application/json")) {
          const text = await res.data.text();
          let msg = "Não foi possível exportar o relatório.";
          try {
            const parsed = JSON.parse(text);
            msg = parsed.err || parsed.message || msg;
          } catch (_e) {
            /* mantém msg padrão */
          }
          this.$vueOnToast.pop("error", msg);
          return;
        }
        const filename = this.extrairNomeArquivo(
          res.headers && res.headers["content-disposition"],
          fallbackName
        );
        const blob = new Blob([res.data], {
          type: contentType || "application/octet-stream"
        });
        if (window.navigator.msSaveBlob) {
          window.navigator.msSaveOrOpenBlob(blob, filename);
        } else {
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(link.href);
        }
      } catch (err) {
        const msg =
          (err.response && err.response.data && err.response.data.err) ||
          err.message ||
          "Não foi possível exportar o relatório.";
        this.$vueOnToast.pop(
          "error",
          typeof msg === "string"
            ? msg
            : "Não foi possível exportar o relatório."
        );
      } finally {
        this.exportando = false;
        this.formatoExportacao = "";
      }
    },
    formatarCpfCnpj(value) {
      if (value == null || String(value).trim() === "") {
        return "";
      }
      const sanitized = sanitizeDocument(value);
      if (!sanitized) {
        return "";
      }
      if (sanitized.length === 11 && /^\d{11}$/.test(sanitized)) {
        return sanitized.replace(
          /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
          "$1.$2.$3-$4"
        );
      }
      if (sanitized.length >= 14 || /[A-Z]/.test(sanitized)) {
        return formatarDocumentoCnpjEstiloClienteForm(sanitized);
      }
      if (sanitized.length > 11) {
        return formatarDocumentoCnpjEstiloClienteForm(sanitized);
      }
      return sanitized;
    },
    formatarSaldo(value) {
      if (value == null || value === "") {
        return "";
      }
      const n = parseFloat(String(value).replace(",", "."));
      if (!Number.isFinite(n)) {
        return String(value);
      }
      return n.toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    },
    formatDateTime(v) {
      if (v == null || v === "") {
        return "";
      }
      return moment(v).isValid() ? moment(v).format("DD/MM/YYYY HH:mm") : v;
    }
  }
};
</script>

<style scoped>
.saldo-pontos__cliente-meta {
  font-size: 12px;
  opacity: 0.85;
  line-height: 1.35;
}

.pr-cfg-head {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
}

.pr-cfg-title {
  font-weight: 600;
}

.pr-cfg-card--dark .pr-cfg-title {
  color: rgba(255, 255, 255, 0.95);
}

.pr-cfg-head-spacer {
  display: inline-block;
  width: 36px;
  flex-shrink: 0;
}

.pr-section-label {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
}

.pr-cfg-card--dark .pr-section-label {
  color: rgba(255, 255, 255, 0.72);
}

.pr-cat-box {
  border: 1px solid rgba(25, 118, 210, 0.35);
  border-radius: 8px;
  background: rgba(25, 118, 210, 0.02);
}

.pr-cfg-card--dark .pr-cat-box {
  border-color: rgba(100, 181, 246, 0.45);
  background: rgba(18, 24, 32, 0.65);
}

.pr-hint {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin: 0;
  line-height: 1.45;
}

.pr-cfg-card--dark .pr-hint {
  color: rgba(255, 255, 255, 0.65);
}

.pr-cfg-card-text {
  position: relative;
}

.pr-cfg-card--dark ::v-deep .v-divider {
  border-color: rgba(255, 255, 255, 0.08) !important;
}
</style>
